import { Injectable } from '@angular/core';
import { ToastrService, IndividualConfig, ToastRef, ActiveToast } from 'ngx-toastr';
import { Subject ,  Observable } from 'rxjs';
import { AppMessage } from './app-message';
import { environment } from '../../../environments/environment';
import { StateService } from '../state/state.service';
import { QmReplacePipe } from '../../components/qm-pipes/qm-replace.pipe';
import { TranslateService } from '@ngx-translate/core';
import { Reminder } from '../../reminder/reminder.model';
import { _ } from '../../shared/utils';
import { format } from 'date-fns';
import fi from 'date-fns/locale/fi';
import { PushNotificationsService } from './push-notifications.service';
import { SidebarService } from '../sidebar/sidebar.service';

@Injectable()
export class AppMessageService {

  /**
   * alarm listener Subject
   * @type {Subject<any>}
   */
  private alarmData = new Subject<AppMessage>();
  /**
   * Observable for alarm listener Subject
   * @type {Observable<any>}
   */
  public alarmChanged$: Observable<AppMessage> = this.alarmData.asObservable();

   /**
   * References to currently displayed messages by reminder id.
   */
  public reminders = new Map<number, {toastRef: ToastRef<any>, notificationRef: Notification}>();

  constructor(
    private toastr: ToastrService,
    private stateService: StateService,
    private push: PushNotificationsService,
    private translate: TranslateService,
    private sidebarService: SidebarService) {
  }

  /**
   * Add new status listener
   * @param alarmMessage
   */
  public broadcastAlarm(alarmMessage: AppMessage) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      alarmMessage.message = this.getTranslation(alarmMessage.message);
      /// NOTE: info level messages are not translated ?
      const toastConfigurationForAlarm: Partial<IndividualConfig> = {
        timeOut: 10 * 1000,
        closeButton: true,
        tapToDismiss: false
      };

      if (alarmMessage.toast) {
        switch (alarmMessage.priority) {
          case 'success':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-success').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          case 'warning':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-warning').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          case 'error':
            if (alarmMessage.translations) {
              alarmMessage.message = new QmReplacePipe().transform(alarmMessage.message, alarmMessage.translations);
            }
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-custom-error').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
          default:
            this.toastr.show(alarmMessage.message, undefined, toastConfigurationForAlarm, 'toast-info').onTap.subscribe(() => this.handleToastTapped(alarmMessage));
            break;
        }
      }
    }
    this.alarmData.next(alarmMessage);
  }

  public broadcastReminder(reminder: Reminder) {
    if (!this.stateService.getPartOfState('settingsState').screenMode) {
      const message = reminder.backendReminder.description;
      const toastConfigurationForReminder: Partial<IndividualConfig> = {
        disableTimeOut: true,
        closeButton: true,
        tapToDismiss: false
      };

      let title = this.getTranslation(_('Reminder'));
      if (reminder.backendReminder.alarmingTime) {
        title += ' ';
        title += this.formatDate(reminder.backendReminder.alarmingTime);
      }

      if (this.reminders.has(reminder.backendReminder.id)) {
        return;
      } else {
        this.reminders.set(reminder.backendReminder.id, {notificationRef: null, toastRef: null});
      }

      this.push.create('Mipro REGO', { // Push notification desktop
        body: reminder.backendReminder.description,
        icon: environment.assetRoot + '/favicon/192.png',
      }).subscribe(({ notification }) => { // Toast on browser
        const activeToast = this.toastr.show(message, title, toastConfigurationForReminder, 'toast-reminder');
        this.reminders.set(reminder.backendReminder.id, {notificationRef: notification, toastRef: activeToast.toastRef});
        activeToast.onHidden.subscribe(() => {
          reminder.cancelAlarm();
          notification.close();
        });
      }, err => { // If notification was rejected (no permissions for instance)
        console.log(err);
        const toast = this.toastr.show(message, title, toastConfigurationForReminder, 'toast-reminder');
        toast.onHidden.subscribe(() => {
          reminder.cancelAlarm();
        });
        this.reminders.set(reminder.backendReminder.id, {notificationRef: null, toastRef: toast.toastRef});
      });
    }
  }

  public hideReminder(reminder: Reminder) {
    if (this.reminders.has(reminder.backendReminder.id)) {
      const {notificationRef, toastRef} = this.reminders.get(reminder.backendReminder.id);
      this.reminders.delete(reminder.backendReminder.id);
      if (toastRef) {
        toastRef.close();
      }
      if (notificationRef) {
        notificationRef.close();
      }
    }
  }

  private getTranslation(msg: string): string {
    try {
      return this.translate.instant(msg);
    } catch (e) {
      return msg;
    }
  }

  private formatDate(value: Date): string {
    const form = 'HH:mm';
    if (this.stateService.getPartOfState('settingsState').language === 'fi') {
      return format(value, form, {locale: fi});
    } else {
      return format(value, form);
    }
  }

  private handleToastTapped(appMessage: AppMessage) {
    this.sidebarService.requestSidebarPanel(appMessage.component);
  }
}
